import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ConfigConstants, isDefined } from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, take, takeUntil, withLatestFrom } from 'rxjs/operators';
import { updateDefectServicePartIdentificationField } from '../../store/actions/questionnaire.actions';
import { getFormDefectServicePartIdentification } from '../../store/selectors/form.selector';
import { FormsState } from '../../store/state/pace-forms.state';
import { DefectServicePartIdentification } from '../../types/defect-service-part-identification';

@Component({
    selector: 'pace-forms-defect-service-part-identification',
    templateUrl: './defect-service-part-identification.component.html',
    styleUrls: ['./defect-service-part-identification.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class DefectServicePartIdentificationComponent implements OnInit, OnDestroy {
    @Input()
    public parentForm: FormArray;

    public defectServicePartIdentification$: Observable<DefectServicePartIdentification>;
    private destroyTrigger$: Subject<void>;

    public defectServicePartIdentificationGroup: FormGroup;
    public serialNoControl: FormControl;
    public materialNoControl: FormControl;
    public orderNoControl: FormControl;
    public constructor(
        private readonly store: Store<FormsState>,
        private readonly formBuilder: FormBuilder,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly cdr: ChangeDetectorRef,
    ) {
        this.defectServicePartIdentification$ = this.store
            .select(getFormDefectServicePartIdentification)
            .pipe(filter(isDefined));
        this.destroyTrigger$ = new Subject<void>();
    }

    public ngOnInit(): void {
        /* eslint-disable @typescript-eslint/unbound-method */
        this.serialNoControl = this.formBuilder.control({}, Validators.required);
        this.materialNoControl = this.formBuilder.control({}, Validators.required);
        this.orderNoControl = this.formBuilder.control({}, Validators.required);
        /* eslint-enable @typescript-eslint/unbound-method */

        this.defectServicePartIdentificationGroup = this.formBuilder.group({
            serialNoControl: this.serialNoControl,
            materialNoControl: this.materialNoControl,
            orderNoControl: this.orderNoControl,
        });

        Promise.resolve(undefined)
            .then(() => {
                this.parentForm.push(this.defectServicePartIdentificationGroup);
            })
            .catch((error: Error) => {
                this.log.error(
                    'DefectServicePartIdentificationComponent: ngOnInit: Error in parentForm.push: ',
                    JSON.stringify(error),
                );
            });

        this.serialNoControl.valueChanges
            .pipe(
                debounceTime(ConfigConstants.debounceTimeMs),
                distinctUntilChanged(),
                withLatestFrom(this.defectServicePartIdentification$.pipe(take(1))),
                takeUntil(this.destroyTrigger$),
            )
            .subscribe(([newValue, defectServicePartIdentification]) => {
                this.store.dispatch(
                    updateDefectServicePartIdentificationField({
                        xmlId: defectServicePartIdentification.serialNo.xmlId,
                        newValue,
                    }),
                );
                this.defectServicePartIdentificationGroup.updateValueAndValidity();
                this.cdr.detectChanges();
            });

        this.materialNoControl.valueChanges
            .pipe(
                debounceTime(ConfigConstants.debounceTimeMs),
                distinctUntilChanged(),
                withLatestFrom(this.defectServicePartIdentification$.pipe(take(1))),
                takeUntil(this.destroyTrigger$),
            )
            .subscribe(([newValue, defectServicePartIdentification]) => {
                this.store.dispatch(
                    updateDefectServicePartIdentificationField({
                        xmlId: defectServicePartIdentification.materialNo.xmlId,
                        newValue,
                    }),
                );
                this.defectServicePartIdentificationGroup.updateValueAndValidity();
                this.cdr.detectChanges();
            });

        this.orderNoControl.valueChanges
            .pipe(
                debounceTime(ConfigConstants.debounceTimeMs),
                distinctUntilChanged(),
                withLatestFrom(this.defectServicePartIdentification$.pipe(take(1))),
                takeUntil(this.destroyTrigger$),
            )
            .subscribe(([newValue, defectServicePartIdentification]) => {
                this.store.dispatch(
                    updateDefectServicePartIdentificationField({
                        xmlId: defectServicePartIdentification.orderNo.xmlId,
                        newValue,
                    }),
                );
                this.defectServicePartIdentificationGroup.updateValueAndValidity();
                this.cdr.detectChanges();
            });

        this.defectServicePartIdentification$
            .pipe(filter(isDefined), takeUntil(this.destroyTrigger$))
            .subscribe((defectServicePartIdentification) => {
                this.materialNoControl.patchValue(defectServicePartIdentification.materialNo.value, {
                    emitEvent: false,
                });

                this.serialNoControl.patchValue(defectServicePartIdentification.serialNo.value, {
                    emitEvent: false,
                });

                this.orderNoControl.patchValue(defectServicePartIdentification.orderNo.value, {
                    emitEvent: false,
                });
            });
    }

    public ngOnDestroy(): void {
        if (this.parentForm) {
            const index = this.parentForm.controls.findIndex(
                (control) => control === this.defectServicePartIdentificationGroup,
            );
            this.parentForm.removeAt(index);
        }

        this.destroyTrigger$.next();
    }
}
